import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { parse as parseQuery } from '../../services/query-string/index'
import { historyRedirect, HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { makeIcon } from '../OracleCommons/icons'
import { Location, History } from './history-types'
import { OracleMobilePersonalityMenu } from './OracleMobilePersonalityMenu'
import { ParsedSeedQuestion } from './hooks/seedQuestionTagUtils'

interface OracleMobileDrawerButtonProps {
  onClick: () => void
  className?: string
  location: Location
  history: History
  isInConversation: boolean
  isEnglishUser: boolean
  tarotStartSeed?: ParsedSeedQuestion | null
  onTarotNewReading?: () => void
}

export const OracleMobileDrawerButton: React.FC<OracleMobileDrawerButtonProps> = ({
  onClick,
  className = '',
  location,
  history,
  isInConversation,
  isEnglishUser,
  tarotStartSeed,
  onTarotNewReading,
}) => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectPrimaryLanguage)
  const [showPersonalityMenu, setShowPersonalityMenu] = useState(false)
  const newChatButtonRef = useRef<HTMLButtonElement>(null)

  const aiGuideMenuText = useTranslatedText({
    keys: ['mobileDrawer', 'aiGuideMenu'],
  })
  const newChatLabel = useTranslatedText({ keys: ['mobileDrawer', 'newChat'] })

  const handleNewChatClick = (event: React.MouseEvent) => {
    event.stopPropagation()

    if (isEnglishUser) {
      setShowPersonalityMenu((prev) => !prev)
      return
    }

    // Non-English users: navigate to fresh chat by removing chatSessionId from URL
    const currentQuery = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { chatSessionId, ...queryWithoutChatSession } = currentQuery

    historyRedirect({
      history,
      url: URL_AI_CHAT,
      query: queryWithoutChatSession,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
  }

  return (
    <div className={`oracle-mobile-drawer-button-container ${className}`}>
      <div className="oracle-mobile-drawer-button">
        <button
          className="oracle-mobile-drawer-button__conversations"
          onClick={onClick}
          type="button"
          aria-label={aiGuideMenuText}
        >
          <div className="oracle-mobile-drawer-button__icon">
            {makeIcon('panel')}
          </div>
          <span className="oracle-mobile-drawer-button__text">
            {aiGuideMenuText}
          </span>
        </button>
        {isInConversation && tarotStartSeed && (
          <button
            className="oracle-mobile-drawer-button__new-reading"
            onClick={() => onTarotNewReading?.()}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {tarotStartSeed.question}
          </button>
        )}
        {isInConversation && !tarotStartSeed && (
          <button
            ref={newChatButtonRef}
            className="oracle-mobile-drawer-button__new-chat"
            onClick={handleNewChatClick}
            type="button"
            aria-label={newChatLabel}
          >
            {makeIcon('new-chat')}
          </button>
        )}
      </div>
      {isInConversation && !tarotStartSeed && isEnglishUser && (
        <OracleMobilePersonalityMenu
          isVisible={showPersonalityMenu}
          onClose={() => setShowPersonalityMenu(false)}
          location={location}
          history={history}
          triggerRef={newChatButtonRef}
        />
      )}
    </div>
  )
}
